import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, CheckCircle2 } from "lucide-react";
import { ProvinciaSelect } from "@/components/ProvinciaSelect";
import { toast } from "sonner";

const GRADI = [
  "Dirigente Superiore", "Dirigente", "Com. Territoriale", "Com. Distaccamento",
  "Sottufficiale", "Agente", "Agente scelto", "Operatore",
];

const FIELDS = [
  { key: "stato", label: "Stato", type: "stato" },
  { key: "ruolo_grado", label: "Ruolo/Grado", type: "grado" },
  { key: "distaccamento", label: "Distaccamento", type: "text" },
  { key: "provincia", label: "Provincia", type: "provincia" },
  { key: "scadenza_nomina", label: "Scadenza nomina", type: "date" },
  { key: "scadenza_tesserino", label: "Scadenza tesserino", type: "date" },
  { key: "numero_decreto", label: "Numero decreto", type: "text" },
  { key: "data_rilascio_decreto", label: "Data rilascio decreto", type: "date" },
  { key: "quota_pagata_anno", label: "Quota associativa", type: "quota" },
];

export function BulkEditDialog({ selectedIds = [], filteredIds = [], trigger, onDone }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scope, setScope] = useState("selected");
  const [enabled, setEnabled] = useState({});
  const [values, setValues] = useState({});
  const [result, setResult] = useState(null);

  const hasSelection = selectedIds.length > 0;
  const effectiveScope = hasSelection ? scope : "filtered";
  const targetIds = effectiveScope === "selected" ? selectedIds : filteredIds;
  const year = new Date().getFullYear();

  const reset = () => { setEnabled({}); setValues({}); setScope("selected"); setResult(null); };

  const submit = async () => {
    const fields = {};
    for (const f of FIELDS) {
      if (!enabled[f.key]) continue;
      let v = values[f.key];
      if (f.type === "stato") v = v || "attiva";
      else if (f.type === "grado") v = v || GRADI[0];
      else if (f.type === "quota") v = (v || "pagata") === "pagata" ? year : null;
      else v = (v === undefined || v === "") ? null : v;
      fields[f.key] = v;
    }
    if (Object.keys(fields).length === 0) { toast.error("Seleziona almeno un campo da modificare"); return; }
    if (targetIds.length === 0) { toast.error("Nessuna guardia da aggiornare"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/guardie/bulk-update", { ids: targetIds, fields });
      toast.success(`${data.modificati} schede aggiornate su ${data.totali}`);
      qc.invalidateQueries({ queryKey: ["guardie"] });
      qc.invalidateQueries({ queryKey: ["scadenze"] });
      qc.invalidateQueries({ queryKey: ["payments"] });
      setResult(data);
      onDone?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const closeDialog = () => { setOpen(false); reset(); };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto gz-scroll">
        <DialogHeader><DialogTitle>Modifica massiva schede</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-4 py-2" data-testid="bulk-result">
            <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
              <div>
                <p className="font-heading font-bold text-slate-900">Aggiornate {result.modificati} schede su {result.totali}</p>
                <p className="text-sm text-slate-600">Operazione completata correttamente.</p>
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Guardie interessate</p>
              <div className="max-h-64 space-y-1 overflow-y-auto gz-scroll rounded-lg border border-slate-200 p-2">
                {(result.guardie || []).map((g) => (
                  <div key={g.id} className="rounded-md px-3 py-1.5 text-sm text-slate-700 odd:bg-slate-50" data-testid={`bulk-result-${g.id}`}>
                    {g.cognome} {g.nome}
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button onClick={closeDialog} data-testid="bulk-close-button" className="bg-[#14382C] hover:bg-[#0E281F]">Chiudi</Button>
            </DialogFooter>
          </div>
        ) : (
        <>
        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="bulk-scope-box">
            {hasSelection ? (
              <div className="space-y-2">
                <p className="font-medium text-slate-700">Applica a:</p>
                <div className="flex flex-col gap-1.5 text-slate-600">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="bulk-scope" className="accent-[#14382C]" checked={scope === "selected"} onChange={() => setScope("selected")} data-testid="bulk-scope-selected" />
                    Guardie selezionate ({selectedIds.length})
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="bulk-scope" className="accent-[#14382C]" checked={scope === "filtered"} onChange={() => setScope("filtered")} data-testid="bulk-scope-filtered" />
                    Tutte le guardie filtrate ({filteredIds.length})
                  </label>
                </div>
              </div>
            ) : (
              <p className="text-slate-600">Verranno aggiornate <strong>tutte le {filteredIds.length} guardie filtrate</strong>. Spunta delle righe per limitare la selezione.</p>
            )}
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Campi da aggiornare</p>
            {FIELDS.map((f) => (
              <div key={f.key} className="flex items-center gap-3">
                <input
                  type="checkbox"
                  className="h-4 w-4 flex-shrink-0 accent-[#14382C]"
                  checked={!!enabled[f.key]}
                  onChange={(e) => setEnabled((s) => ({ ...s, [f.key]: e.target.checked }))}
                  data-testid={`bulk-enable-${f.key}`}
                />
                <Label className="w-40 flex-shrink-0 text-slate-700">{f.label}</Label>
                {f.type === "stato" ? (
                  <Select value={values[f.key] || "attiva"} onValueChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} disabled={!enabled[f.key]}>
                    <SelectTrigger className="flex-1" data-testid={`bulk-value-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="attiva">Attiva</SelectItem>
                      <SelectItem value="disattivata">Non attiva</SelectItem>
                    </SelectContent>
                  </Select>
                ) : f.type === "grado" ? (
                  <Select value={values[f.key] || GRADI[0]} onValueChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} disabled={!enabled[f.key]}>
                    <SelectTrigger className="flex-1" data-testid={`bulk-value-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GRADI.map((gr) => <SelectItem key={gr} value={gr}>{gr}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "provincia" ? (
                  <ProvinciaSelect className="flex-1" value={values[f.key]} onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} disabled={!enabled[f.key]} testid={`bulk-value-${f.key}`} />
                ) : f.type === "quota" ? (
                  <Select value={values[f.key] || "pagata"} onValueChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} disabled={!enabled[f.key]}>
                    <SelectTrigger className="flex-1" data-testid={`bulk-value-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pagata">Pagata</SelectItem>
                      <SelectItem value="non_pagata">Non pagata</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={f.type === "date" ? "date" : "text"}
                    className="flex-1"
                    disabled={!enabled[f.key]}
                    value={values[f.key] || ""}
                    onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                    data-testid={`bulk-value-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="bulk-apply-button" className="bg-[#14382C] hover:bg-[#0E281F]">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Applica modifiche
          </Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
